import type { DomainAffiliateEntry, Option } from './types';

/**
 * ZET-4737: a domain (or tag) may appear in only one Domain-specific Affiliates entry.
 * Finds every domain value that two or more entries share, so the pane can flag the rows
 * and the validator can block the save.
 */
export interface DuplicateDomain {
  /** The selected value, as stored on the entry. */
  value: string;
  /** Zero-based indexes of every entry that selected it, in order. */
  entryIndexes: number[];
}

export const DUPLICATE_DOMAIN_FIELD_ERROR = 'Each domain can appear in one entry only.';

export function findDuplicateDomains(entries: DomainAffiliateEntry[] | null | undefined): DuplicateDomain[] {
  const seen = new Map<string, number[]>();
  (entries ?? []).forEach((entry, index) => {
    // The same value twice inside one entry is not a cross-entry duplicate.
    new Set(entry?.domains ?? []).forEach((value) => {
      if (!value) return;
      const list = seen.get(value) ?? [];
      list.push(index);
      seen.set(value, list);
    });
  });
  return [...seen.entries()]
    .filter(([, indexes]) => indexes.length > 1)
    .map(([value, entryIndexes]) => ({ value, entryIndexes }));
}

/** The duplicates that involve one entry: its index is in entryIndexes. */
export function duplicatesForEntry(duplicates: DuplicateDomain[], index: number): DuplicateDomain[] {
  return duplicates.filter((dupe) => dupe.entryIndexes.includes(index));
}

const entryNumbers = (indexes: number[]): string => {
  const numbers = indexes.map((i) => i + 1);
  if (numbers.length <= 1) return numbers.join('');
  return `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
};

/** "Also selected in entry 2. Each domain can appear in one entry only." for the field under entry `index`. */
export function duplicateFieldMessage(duplicates: DuplicateDomain[], index: number): string | undefined {
  const mine = duplicatesForEntry(duplicates, index);
  if (mine.length === 0) return undefined;
  const others = [...new Set(mine.flatMap((d) => d.entryIndexes).filter((i) => i !== index))].sort((a, b) => a - b);
  const noun = others.length === 1 ? 'entry' : 'entries';
  return `Also selected in ${noun} ${entryNumbers(others)}. ${DUPLICATE_DOMAIN_FIELD_ERROR}`;
}

/**
 * The dropdown options for entry `index`: a domain already picked by another entry stays in the
 * list but greyed out, with a note naming that entry (SiteOps' choice over hiding it).
 */
export function markDomainsTakenElsewhere(
  options: Option[],
  entries: DomainAffiliateEntry[] | null | undefined,
  index: number
): Option[] {
  const takenBy = new Map<string, number>();
  (entries ?? []).forEach((entry, i) => {
    if (i === index) return;
    (entry?.domains ?? []).forEach((value) => {
      if (!takenBy.has(value)) takenBy.set(value, i);
    });
  });
  if (takenBy.size === 0) return options;
  return options.map((option) => {
    const owner = takenBy.get(option.value);
    return owner === undefined ? option : { ...option, disabled: true, note: `Already in entry ${owner + 1}` };
  });
}

/** Title and body for the banner above the entries. */
export function duplicateBanner(
  duplicates: DuplicateDomain[],
  options: Option[]
): { title: string; body: string } | undefined {
  if (duplicates.length === 0) return undefined;
  const labelFor = (value: string) => options.find((o) => o.value === value)?.label ?? value;
  const allIndexes = [...new Set(duplicates.flatMap((d) => d.entryIndexes))].sort((a, b) => a - b);
  const title =
    duplicates.length === 1
      ? `Duplicate domain in entries ${entryNumbers(allIndexes)}`
      : `Duplicate domains in entries ${entryNumbers(allIndexes)}`;
  const details = duplicates
    .map((d) => `Entries ${entryNumbers(d.entryIndexes)} use the same domain (${labelFor(d.value)}).`)
    .join(' ');
  return {
    title,
    body: `${details} A domain can only appear in one entry. Change or remove the extra entries before saving.`,
  };
}
